import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { LoadingBlock, Spinner } from '../../components/Spinner'
import { ConfirmPopover } from '../../library/ConfirmPopover'
import { PhoneSheet } from '../../chats/phone/PhoneSheet'
import { formatDuration, formatUsd, type Attachment, type MediaItem, type Playable } from '../data'
import { useFanslyDeps } from '../deps'
import { FsThumb, FsViewer } from '../ui/FsMedia'
import { likedByFan, likedByMe, type ChatMessage } from './model'
import type { PendingMessage, Thread } from './useThread'

export interface Translation {
  state: 'busy' | 'done' | 'error'
  text: string
}

interface Props {
  thread: Thread
  fanId: string
  fanName: string
  meId: string
  canWrite: boolean
  /** Phones: press and hold a message for its actions. */
  holdToAct: boolean
  translations: Map<string, Translation>
  onTranslate: (m: ChatMessage) => void
  /** Copy a message's text into the composer. */
  onQuote?: (text: string) => void
}

const HOLD_MS = 430
const HOLD_SLOP = 8
const NOT_A_HOLD = 'button, a, video, audio, input, textarea'

function dayKey(ms: number) {
  return new Date(ms).toDateString()
}

function dayLabel(ms: number, locale: string, t: (k: string) => string): string {
  const d = new Date(ms)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return t('thread.today')
  if (d.toDateString() === yesterday.toDateString()) return t('thread.yesterday')
  return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' })
}

const timeLabel = (ms: number, locale: string) => (ms ? new Date(ms).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '')

/** A voice note: play/pause, a seek bar and the time, streamed through the CRM on first play. */
export function VoicePlayer({ media }: { media: Playable }) {
  const { t } = useTranslation('fanslyChats')
  const { streamMedia } = useFanslyDeps()
  const audio = useRef<HTMLAudioElement>(null)
  const [src, setSrc] = useState('')
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'paused' | 'error'>('idle')
  const [pos, setPos] = useState(0)
  const [len, setLen] = useState(media.duration || 0)

  const toggle = async () => {
    const el = audio.current
    if (state === 'playing') {
      el?.pause()
      return
    }
    if (src) {
      void el?.play().catch(() => setState('error'))
      return
    }
    if (!media.src) return setState('error')
    setState('loading')
    try {
      const url = media.src.startsWith('blob:') ? media.src : await streamMedia(media.src)
      setSrc(url)
    } catch {
      setState('error')
    }
  }

  useEffect(() => {
    if (src && audio.current) void audio.current.play().catch(() => setState('error'))
  }, [src])

  return (
    <div className="fsc-voice">
      <button type="button" className="fsc-voice-play" aria-label={state === 'playing' ? t('voice.pause') : t('voice.play')} onClick={() => void toggle()} disabled={state === 'loading'}>
        {state === 'loading' ? <Spinner size={14} label={t('voice.loading')} /> : <FeatherIcon name={state === 'playing' ? 'pause' : 'play'} size={15} />}
      </button>
      <input
        type="range"
        className="fsc-voice-seek"
        min={0}
        max={1000}
        value={len ? Math.round((pos / len) * 1000) : 0}
        aria-label={t('voice.seek')}
        disabled={!src}
        onChange={(e) => {
          const el = audio.current
          if (el && len) el.currentTime = (Number(e.target.value) / 1000) * len
        }}
      />
      <span className="fsc-voice-time">{state === 'error' ? t('voice.failed') : `${formatDuration(pos)} / ${formatDuration(len)}`}</span>
      {src && (
        <audio
          ref={audio}
          src={src}
          preload="metadata"
          controlsList="nodownload"
          onPlay={() => setState('playing')}
          onPause={() => setState('paused')}
          onEnded={() => (setState('paused'), setPos(0))}
          onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => Number.isFinite(e.currentTarget.duration) && setLen(e.currentTarget.duration)}
          onError={() => setState('error')}
        />
      )}
    </div>
  )
}

/** A thumbnail of a file on this device (a send still going) or a Fansly one. */
export function AnyThumb({ media, onOpen, price }: { media: Playable; onOpen?: () => void; price?: number }) {
  const local = /^(blob|data):/.test(media.poster || media.src)
  if (!local) return <FsThumb media={media} onOpen={onOpen} price={price} />
  return (
    <span className="fs-thumb fsc-local-thumb">
      {media.kind === 'image' || media.poster ? <img className="fs-thumb-img" src={media.poster || media.src} alt="" /> : <FeatherIcon name={media.kind === 'audio' ? 'mic' : 'film'} size={22} />}
    </span>
  )
}

/** "$12 · Bought" or "$12 · Not bought yet" for a paid item or bundle. */
function PriceState({ price, purchased }: { price: number; purchased: boolean }) {
  const { t, i18n } = useTranslation('fanslyChats')
  return (
    <span className={`rc-price-state ${purchased ? 'is-unlocked' : 'is-locked'}`} title={purchased ? t('thread.boughtHint') : t('thread.notBoughtHint')}>
      <FeatherIcon name={purchased ? 'unlock' : 'lock'} size={12} />
      <b>{formatUsd(price, i18n.language)}</b>
      <span>· {purchased ? t('thread.bought') : t('thread.notBought')}</span>
    </span>
  )
}

/** The things a message attaches: media grid (bundles expanded), voice notes as players. */
function MessageMedia({ attachments, onOpen }: { attachments: Attachment[]; onOpen: (media: Playable) => void }) {
  const items: Array<{ key: string; item: MediaItem }> = []
  const voices: Array<{ key: string; media: Playable }> = []
  for (const a of attachments) {
    const list = a.type === 'bundle' ? a.bundle.items : [a.item]
    for (const item of list) {
      const shown = item.media ?? item.preview
      const key = `${a.type}:${a.type === 'bundle' ? a.bundle.id : ''}:${item.id}`
      if (shown?.kind === 'audio') voices.push({ key, media: shown })
      else items.push({ key, item })
    }
    // A bundle Fansly did not join: its cover, so it never shows as nothing.
    if (a.type === 'bundle' && !a.bundle.items.length && a.bundle.preview) items.push({ key: `b:${a.bundle.id}`, item: { id: a.bundle.id, mediaId: '', media: null, preview: a.bundle.preview, price: 0, purchased: a.bundle.purchased, access: a.bundle.access, likeCount: 0, createdAt: 0, raw: a.bundle.raw } })
  }
  return (
    <>
      {voices.map((v) => (
        <VoicePlayer key={v.key} media={v.media} />
      ))}
      {items.length > 0 && (
        <div className={`rc-media-grid fsc-media-grid ${items.length > 1 ? 'is-multi' : ''}`}>
          {items.map(({ key, item }) => {
            const shown = item.media ?? item.preview
            return <FsThumb key={key} media={shown} locked={!item.access || !item.media} onOpen={shown ? () => onOpen(item.media ?? shown) : undefined} />
          })}
        </div>
      )}
    </>
  )
}

/** The price of what a message sells, if anything: the bundle's, else its items'. */
function salesOf(m: ChatMessage): { price: number; purchased: boolean } | null {
  for (const a of m.attachments) {
    if (a.type === 'bundle' && a.bundle.price > 0) return { price: a.bundle.price, purchased: a.bundle.purchased }
  }
  const priced = m.attachments.flatMap((a) => (a.type === 'media' ? [a.item] : a.bundle.items)).filter((i) => i.price > 0)
  if (!priced.length) return null
  return { price: priced.reduce((s, i) => s + i.price, 0), purchased: priced.every((i) => i.purchased) }
}

interface Action {
  key: string
  icon: string
  label: string
  danger?: boolean
  run: () => void
}

function MessageMenu({ actions, mine }: { actions: Action[]; mine: boolean }) {
  const { t } = useTranslation('fanslyChats')
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false)
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('mousedown', close)
    window.addEventListener('keydown', esc)
    return () => {
      window.removeEventListener('mousedown', close)
      window.removeEventListener('keydown', esc)
    }
  }, [open])
  if (!actions.length) return null
  return (
    <div className={`rc-msg-menu ${mine ? 'is-mine' : ''} ${open ? 'is-open' : ''}`} ref={box}>
      <button type="button" className="rc-msg-menu-btn" title={t('menu.more')} aria-label={t('menu.more')} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <FeatherIcon name="more-horizontal" size={14} />
      </button>
      {open && (
        <div className="rc-msg-menu-list" role="menu">
          {actions.map((a) => (
            <button key={a.key} type="button" role="menuitem" className={a.danger ? 'is-danger' : ''} onClick={() => (setOpen(false), a.run())}>
              <FeatherIcon name={a.icon} size={13} />
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function PendingBubble({ p, onRetry, onDiscard }: { p: PendingMessage; onRetry: () => void; onDiscard: () => void }) {
  const { t, i18n } = useTranslation('fanslyChats')
  return (
    <>
      <div className={`rc-msg is-mine ${p.state === 'failed' ? 'is-failed' : p.state === 'sending' ? 'is-sending' : ''}`} data-pending-id={p.tempId}>
        {p.voice && (
          <span className="fsc-voice is-pending">
            <FeatherIcon name="mic" size={14} />
            {t('voice.note')}
          </span>
        )}
        {p.media.length > 0 && (
          <div className={`rc-media-grid fsc-media-grid ${p.media.length > 1 ? 'is-multi' : ''}`}>
            {p.media.map((m, i) => (
              <AnyThumb key={`${m.mediaId}:${i}`} media={m} />
            ))}
          </div>
        )}
        {p.gif && <img className="rc-gif" src={p.gif.src} alt={p.gif.title || t('gif.one')} />}
        {p.content && <span className="rc-text">{p.content}</span>}
        {p.price > 0 && (
          <span className="rc-msg-foot">
            <span className="rc-price-state is-locked">
              <FeatherIcon name="lock" size={12} />
              <b>{formatUsd(p.price, i18n.language)}</b>
            </span>
          </span>
        )}
      </div>
      {p.state === 'sending' && (
        <span className="rc-meta is-mine rc-meta--pending">
          <Spinner size={11} label={t('thread.sending')} />
        </span>
      )}
      {p.state === 'sent' && <span className="rc-meta is-mine">{t('thread.sent')}</span>}
      {p.state === 'failed' && (
        <span className="rc-meta is-mine rc-meta--failed" title={p.error}>
          <FeatherIcon name="alert-circle" size={12} />
          {p.error || t('thread.notSent')}
          <button type="button" onClick={onRetry}>
            {t('thread.retry')}
          </button>
          <button type="button" onClick={onDiscard}>
            {t('thread.discard')}
          </button>
        </span>
      )}
    </>
  )
}

/**
 * The open chat's messages (the OnlyFans chat window's look): day lines,
 * bubbles in and out, media (bundles expanded, PPV price and whether the fan
 * bought it), voice notes, tips, GIFs, likes, translations, and the sends
 * still going. Stays at the bottom for new messages and keeps its place when
 * older ones load above.
 */
export function Transcript({ thread, fanId, fanName, meId, canWrite, holdToAct, translations, onTranslate, onQuote }: Props) {
  const { t, i18n } = useTranslation('fanslyChats')
  const { messages, pending, loading, loadingOlder, hasOlder, error, loadOlder } = thread
  const scroller = useRef<HTMLDivElement>(null)
  const inner = useRef<HTMLDivElement>(null)
  const top = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)
  const prevHeight = useRef(0)
  const prevFirst = useRef<string | null>(null)
  const [viewing, setViewing] = useState<Playable | null>(null)
  const [deleting, setDeleting] = useState<ChatMessage | null>(null)
  const [held, setHeld] = useState<ChatMessage | null>(null)
  const hold = useRef<{ id: number; x: number; y: number; timer: number } | null>(null)

  // Stay at the bottom for new messages; keep the view still when older ones load above.
  useLayoutEffect(() => {
    const el = scroller.current
    if (!el) return
    const first = messages[0]?.id ?? null
    if (prevFirst.current && first !== prevFirst.current && !pinned.current) el.scrollTop += el.scrollHeight - prevHeight.current
    else if (pinned.current) el.scrollTop = el.scrollHeight
    prevFirst.current = first
    prevHeight.current = el.scrollHeight
  }, [messages, pending])

  // Pictures loading in grow the thread: stay pinned while they do.
  useEffect(() => {
    const el = inner.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      const s = scroller.current
      if (!s) return
      if (pinned.current) s.scrollTop = s.scrollHeight
      prevHeight.current = s.scrollHeight
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Older history loads as the top comes near.
  useEffect(() => {
    const el = top.current
    if (!el || !hasOlder || loading) return
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && loadOlder(), { root: scroller.current, rootMargin: '150px 0px 0px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [hasOlder, loading, loadOlder, messages.length])

  const onScroll = () => {
    const el = scroller.current
    if (!el) return
    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
    prevHeight.current = el.scrollHeight
  }

  const stopHold = () => {
    if (hold.current) window.clearTimeout(hold.current.timer)
    hold.current = null
  }
  useEffect(() => stopHold, [])

  const actionsFor = (m: ChatMessage): Action[] => {
    const out: Action[] = []
    if (m.content) {
      out.push({ key: 'copy', icon: 'copy', label: t('menu.copy'), run: () => void navigator.clipboard?.writeText(m.content).catch(() => undefined) })
      out.push({ key: 'translate', icon: 'globe', label: t('menu.translate'), run: () => onTranslate(m) })
      if (onQuote && canWrite && !m.mine) out.push({ key: 'quote', icon: 'corner-up-left', label: t('menu.quote'), run: () => onQuote(m.content) })
    }
    if (canWrite && !m.mine) {
      const liked = likedByMe(m, meId)
      out.push({ key: 'like', icon: 'heart', label: liked ? t('menu.unlike') : t('menu.like'), run: () => void thread.like(m, !liked) })
    }
    if (canWrite && m.mine) out.push({ key: 'delete', icon: 'trash-2', label: t('menu.delete'), danger: true, run: () => setDeleting(m) })
    return out
  }

  const holdHandlers = (m: ChatMessage) =>
    holdToAct
      ? {
          onPointerDown: (e: React.PointerEvent) => {
            if (e.button !== 0 || (e.target as Element).closest(NOT_A_HOLD)) return
            stopHold()
            const timer = window.setTimeout(() => {
              hold.current = null
              navigator.vibrate?.(10)
              setHeld(m)
            }, HOLD_MS)
            hold.current = { id: e.pointerId, x: e.clientX, y: e.clientY, timer }
          },
          onPointerMove: (e: React.PointerEvent) => {
            const h = hold.current
            if (h && h.id === e.pointerId && Math.hypot(e.clientX - h.x, e.clientY - h.y) > HOLD_SLOP) stopHold()
          },
          onPointerUp: stopHold,
          onPointerCancel: stopHold,
          onPointerLeave: stopHold,
          onContextMenu: (e: React.MouseEvent) => {
            if ((e.target as Element).closest(NOT_A_HOLD)) return
            e.preventDefault()
            stopHold()
            setHeld(m)
          },
        }
      : {}

  const shown = messages.filter((m) => !m.deleted || m.mine)
  let lastSeenIdx = -1
  shown.forEach((m, i) => {
    if (m.mine && m.seen) lastSeenIdx = i
  })

  const rows: ReactNode[] = []
  shown.forEach((m, i) => {
    const prev = shown[i - 1]
    const next = shown[i + 1]
    const newDay = !prev || dayKey(prev.createdAt) !== dayKey(m.createdAt)
    const lastOfGroup = !next || next.mine !== m.mine || dayKey(next.createdAt) !== dayKey(m.createdAt) || next.createdAt - m.createdAt > 10 * 60_000
    const sale = salesOf(m)
    const tr = translations.get(m.id)
    const meLiked = likedByMe(m, meId)
    const fanLiked = m.mine && likedByFan(m, fanId)
    rows.push(
      <Fragment key={m.id}>
        {newDay && (
          <div className="rc-day" role="separator">
            <span>{dayLabel(m.createdAt, i18n.language, t)}</span>
          </div>
        )}
        <div className={`rc-msg ${m.mine ? 'is-mine' : ''} ${held?.id === m.id ? 'is-held' : ''} ${m.deleted ? 'fsc-msg-deleted' : ''}`} data-message-id={m.id} {...holdHandlers(m)}>
          {!holdToAct && !m.deleted && <MessageMenu actions={actionsFor(m)} mine={m.mine} />}
          {!m.mine && meLiked && (
            <button type="button" className="rc-like is-liked" aria-label={t('menu.unlike')} title={t('menu.unlike')} disabled={!canWrite} onClick={() => void thread.like(m, false)}>
              <FeatherIcon name="heart" size={13} />
            </button>
          )}
          {fanLiked && (
            <span className="rc-like is-liked fsc-fan-like" role="img" aria-label={t('thread.fanLiked')} title={t('thread.fanLiked')}>
              <FeatherIcon name="heart" size={13} />
            </span>
          )}
          {m.deleted ? (
            <span className="rc-text fsc-deleted">{t('thread.deleted')}</span>
          ) : (
            <>
              {m.tip && (
                <span className="rc-tip">
                  <span className="rc-tip-ic">
                    <FeatherIcon name="dollar-sign" size={15} />
                  </span>
                  <b>{formatUsd(m.tip.amount, i18n.language)}</b>
                  <span className="rc-muted">{t('thread.tip')}</span>
                </span>
              )}
              {m.attachments.length > 0 && <MessageMedia attachments={m.attachments} onOpen={setViewing} />}
              {m.gif && <img className="rc-gif" src={m.gif.src} alt={m.gif.title || t('gif.one')} loading="lazy" />}
              {m.content && <span className="rc-text">{m.content}</span>}
              {tr && <span className={`rc-translation ${tr.state === 'error' ? 'is-error' : ''}`}>{tr.state === 'busy' ? t('translate.busy') : tr.text}</span>}
              {sale && (
                <span className="rc-msg-foot">
                  <PriceState price={sale.price} purchased={sale.purchased} />
                </span>
              )}
            </>
          )}
        </div>
        {lastOfGroup && (
          <span className={`rc-meta ${m.mine ? 'is-mine' : ''}`}>
            {timeLabel(m.createdAt, i18n.language)}
            {i === lastSeenIdx && (
              <>
                {' · '}
                {t('thread.seen')}
                <FeatherIcon name="check-check" size={13} className="rc-seen" />
              </>
            )}
          </span>
        )}
      </Fragment>,
    )
  })

  return (
    <div className={`rc-thread fsc-thread ${holdToAct ? 'is-hold' : ''}`} ref={scroller} onScroll={onScroll}>
      <div className="rc-thread-inner" ref={inner}>
        <div ref={top} className="rc-thread-top">
          {loadingOlder && <Spinner size={16} label={t('thread.loadingOlder')} />}
          {!hasOlder && !loading && messages.length > 0 && <span className="rc-muted">{t('thread.start')}</span>}
        </div>
        {loading && !messages.length && <LoadingBlock pad="40px 0" label={t('thread.loading')} />}
        {error && !loading && !messages.length && (
          <div className="rc-thread-state rc-thread-state--error" role="alert">
            {error}
            <button type="button" className="rc-btn" onClick={thread.reload}>
              {t('retry')}
            </button>
          </div>
        )}
        {!loading && !error && !messages.length && !pending.length && <div className="rc-thread-state">{t('thread.empty', { name: fanName })}</div>}
        {rows}
        {pending.map((p) => (
          <PendingBubble key={p.tempId} p={p} onRetry={() => thread.retry(p.tempId)} onDiscard={() => thread.discard(p.tempId)} />
        ))}
      </div>
      {viewing && <FsViewer media={viewing} onClose={() => setViewing(null)} />}
      {deleting && (
        <ConfirmPopover
          title={t('menu.deleteTitle')}
          body={t('menu.deleteBody')}
          confirmLabel={t('menu.delete')}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            const m = deleting
            setDeleting(null)
            void thread.remove(m)
          }}
        />
      )}
      {holdToAct && (
        <PhoneSheet open={!!held} onClose={() => setHeld(null)} label={t('menu.more')}>
          <div className="ph-menu">
            {held &&
              actionsFor(held).map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className={`ph-menu-item ${a.danger ? 'is-danger' : ''}`}
                  onClick={() => {
                    setHeld(null)
                    a.run()
                  }}
                >
                  <FeatherIcon name={a.icon} size={19} />
                  {a.label}
                </button>
              ))}
          </div>
        </PhoneSheet>
      )}
    </div>
  )
}
